/* eslint-disable require-atomic-updates */
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { now, user } from 'src/StateObserver';
import Scroll from 'src/game/components/shared/Scroll';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../../components/PopupMatch2Base';
import { RacesTimer } from '../components/RacesTimer';
import {
  getActiveRace,
  getRaceOpponents,
  getRaceRewards,
  getRaceStartTime,
} from 'src/replicant/match2/races/races.getters';
import { getRaceTheme, showFakeRaceLoadSpinner } from '../races.utils';
import { TargetRaceConfig } from 'src/replicant/match2/races/races.ruleset';
import {
  TargetRacesEntry,
  RacesEntryParams,
} from '../components/TargetRacesEntry';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

interface PopupMatch2TargetRacesLeaderboardInitOptions extends PopupMatch2BaseInitOptions {
  raceId?: string;
  raceIndex?: number;
}

export default class PopupMatch2TargetRacesLeaderboard extends PopupMatch2Base {
  private readonly scroll: Scroll<RacesEntryParams, TargetRacesEntry>;
  private readonly timer: RacesTimer;
  private config!: TargetRaceConfig;
  private banner: ImageScaleView;
  private description: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const boxWidth = 657;
    const boxHeight = 725;

    super({
      superview: opts.superview,
      width: boxWidth,
      height: boxHeight,
      panelImageOpts: {
        width: boxWidth,
        height: boxHeight,
      },
      offsetY: -175,
      close: () => opts.close(false),
      title: { type: 'image' },
      cta: {
        localeText: () => i18n('match2.tournament.claim').toLocaleUpperCase(),
        onClick: async () => {
          opts.close(true);
        },
        positioning: 'edge',
        offsetY: 25,
      },
      tracking: {
        feature: Feature.MATCH2.TOURNAMENT._,
        $subFeature: Feature.MATCH2.TOURNAMENT.POPUP,
      },
    });

    this.banner = new ImageScaleView({
      superview: this.panel.getSuperview()!,
      infinite: true,
      canHandleEvents: false,
    });

    this.buttonClose!.updateOpts({ superview: this.banner, x: 621 });

    this.title?.updateOpts({
      superview: this.banner,
    });

    this.panel.updateOpts({
      y: 335,
    });

    this.timer = new RacesTimer({
      superview: this.banner,
      x: boxWidth * 0.5,
      y: 307,
      centerOnOrigin: true,
    });

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: 10,
        y: 114,
        width: 637,
        height: 593,
      },
      margin: 2,
      firstItemOffset: 7,
    });

    const finish = new ImageScaleView({
      superview: this.panel,
      centerOnOrigin: true,
      x: 600,
      y: 125,
      image: 'assets/ui/match2/races/common/finish.png',
      width: 93,
      height: 40,
    });

    new LangBitmapFontTextView({
      superview: finish,
      width: 55,
      height: 24,
      centerOnOrigin: true,
      x: finish.style.width * 0.5 - 2,
      y: 15,
      align: 'center',
      verticalAlign: 'center',
      color: '#144F68',
      font: 'Body',
      size: 20,
      localeText: () => i18n('match2.races.finish'),
    });

    this.description = new LangBitmapFontTextView({
      superview: this.panel,
      x: 70,
      width: 500,
      height: 116,
      align: 'center',
      verticalAlign: 'center',
      color: '#ffffff',
      font: 'Body',
      size: 24,
      wordWrap: true,
    });
  }

  public init({
    raceId,
    raceIndex,
    useClickOutside,
  }: PopupMatch2TargetRacesLeaderboardInitOptions) {
    super.init({ useClickOutside });
    const previousRace = !!raceId;
    raceId = raceId ?? getActiveRace(now())?.id;

    const races = user().match2.races[raceId!];
    raceIndex = raceIndex ?? (races?.length ?? 1) - 1;

    // Set timer to 0 for past races.
    if (previousRace) {
      const hasRewards =
        getRaceRewards(user(), raceId!, raceIndex!, now()).length > 0;
      const buttonText = hasRewards ? `match2.tournament.claim` : 'basic.ok';
      this.buttonCta!.localeText = () => i18n(buttonText).toLocaleUpperCase();
      this.buttonCta?.updateOpts({
        zIndex: 1,
        visible: true,
      });
      this.buttonClose?.hide();
      this.timer.setFinished(true);
    } else {
      this.buttonCta?.hide();
      this.buttonClose?.show();
      this.timer.setFinished(false);
    }

    const raceConfig = getActiveRace(getRaceStartTime(raceId!))?.config;
    if (!raceConfig) return;
    this.description.localeText = () =>
      i18n(`match2.races.goals.${raceConfig.type}.${raceConfig.goal}`);

    this.config = raceConfig as TargetRaceConfig;

    this.panel.updateOpts(getRaceTheme(this.config).leaderboard.scrollArea);

    this.setTitleImage({
      ...this.getPopupTitle(),
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.banner.updateOpts({
      ...this.getPopupBanner(),
    });

    const player = {
      name:
        user().publicProfile?.name ??
        user().profile?.name ??
        i18n('match2.tournament.you'),
      avatar:
        user().publicProfile?.photo ?? user().profile?.photo ?? GENERIC_AVATAR,
      score: races[raceIndex!]?.score ?? 0,
      me: true,
    };
    const allPlayers = [
      ...getRaceOpponents(user(), raceId!, raceIndex!, now()),
      player,
    ];
    allPlayers.sort((a, b) => b.score - a.score);

    const { lastViewedRaceScore } = user().match2;
    const lastScores: Record<string, number> = {};
    if (
      lastViewedRaceScore &&
      lastViewedRaceScore.raceId === raceId &&
      lastViewedRaceScore.raceIndex === raceIndex
    ) {
      const oldScores = [
        ...getRaceOpponents(
          user(),
          raceId!,
          raceIndex!,
          lastViewedRaceScore.timestamp,
        ),
        { ...player, score: lastViewedRaceScore.score },
      ];

      oldScores.sort((a, b) => b.score - a.score);
      oldScores.forEach((x) => {
        lastScores[x.name] = x.score;
      });
    }

    this.scroll.setItems(
      allPlayers.map((x, index) => ({
        ...x,
        position: index + 1,
        previousScore: lastScores[x.name] ?? x.score,
      })),
    );

    this.scroll.getView().hide();
  }

  public override async fadeIn() {
    await super.fadeIn();
    await showFakeRaceLoadSpinner(this.panel, '#DA4A4E');
    this.scroll.getView().show();
  }

  private createItem(superview: View, i: number, data: RacesEntryParams) {
    const entry = new TargetRacesEntry(
      { superview },
      this.config,
      getRaceTheme(this.config),
    );
    entry.setup({ ...data, position: i + 1 });
    return entry;
  }

  private setItemData(
    i: number,
    data: RacesEntryParams,
    view: TargetRacesEntry,
  ) {
    view.setup({ ...data, position: i + 1 });
  }

  private getPopupBanner() {
    return getRaceTheme(this.config).leaderboard.background;
  }

  private getPopupTitle() {
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'jp';
    return getRaceTheme(this.config).title[locale];
  }
}
